import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import AppStorage from "@/utils/helpers";

/**
 * Add-on or Extra item within a cart entry
 */
export interface CartItemAddOn {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

/**
 * Cart item interface with nested add-ons and extras
 */
export interface CartItem {
  id: string; // Unique cart entry ID (UUID)
  menuItemId: string; // The actual menu item ID from api
  name: string;
  price: number;
  quantity: number;
  image?: string;
  description?: string;
  size?: string;
  addOns: CartItemAddOn[];
  extras: CartItemAddOn[];
}

/**
 * Payload for adding items to cart
 */
export interface AddToCartPayload {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  description?: string;
  size?: string;
  addOns?: CartItemAddOn[];
  extras?: CartItemAddOn[];
}

interface CartState {
  cart: CartItem[];
  orderData: Record<string, unknown> | null;
  cartModalIsOpen: boolean;
  coupon: string;
  relatedCartItems: CartItem[];
}

/**
 * Generate a unique ID for cart entries
 */
const generateCartId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `cart-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Load cart from storage
 */
const loadCartFromAppStorage = (): CartItem[] => {
  if (typeof window !== "undefined") {
    const cartJSON = AppStorage.get("cart");
    if (cartJSON) {
      try {
        const parsed = JSON.parse(cartJSON);
        // Migration: ensure old cart items have addOns/extras arrays
        return parsed.map((item: CartItem) => ({
          ...item,
          addOns: item.addOns || [],
          extras: item.extras || [],
        }));
      } catch {
        return [];
      }
    }
  }
  return [];
};

/**
 * Save cart to storage
 */
const saveCartToAppStorage = (cart: CartItem[]) => {
  AppStorage.save("cart", JSON.stringify(cart));
};

const initialState: CartState = {
  cart: loadCartFromAppStorage(),
  orderData: null,
  cartModalIsOpen: false,
  coupon: "",
  relatedCartItems: [],
};

/**
 * Cart slice managing shopping cart state
 */
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const { menuItemId, name, price, quantity, image, description, size, addOns, extras } = action.payload;

      // Create new cart entry with unique ID
      const newCartItem: CartItem = {
        id: generateCartId(),
        menuItemId,
        name,
        price,
        quantity: quantity || 1,
        image,
        description,
        size,
        addOns: addOns || [],
        extras: extras || [],
      };

      state.cart.push(newCartItem);
      toast.success("Item added to cart!");
      saveCartToAppStorage(state.cart);
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter((item) => item.id !== action.payload);
      toast.success("Item removed from cart!");
      saveCartToAppStorage(state.cart);
    },
    incrementQuantity: (state, action: PayloadAction<string>) => {
      const item = state.cart.find((item) => item.id === action.payload);
      if (item) {
        item.quantity += 1;
        toast.success("Item quantity increased in cart!");
        saveCartToAppStorage(state.cart);
      }
    },
    decrementQuantity: (state, action: PayloadAction<string>) => {
      const item = state.cart.find((item) => item.id === action.payload);
      if (item) {
        if (item.quantity > 1) {
          item.quantity -= 1;
          toast.success("Item quantity reduced in cart!");
        } else {
          // Remove item from cart when quantity reaches 0
          state.cart = state.cart.filter(
            (cartItem) => cartItem.id !== action.payload
          );
          toast.success("Item removed from cart!");
        }
        saveCartToAppStorage(state.cart);
      }
    },
    updateItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const { id, quantity } = action.payload;
      const item = state.cart.find((item) => item.id === id);
      if (item) {
        if (quantity > 0) {
          item.quantity = quantity;
          toast.success("Cart updated!");
        } else {
          state.cart = state.cart.filter((cartItem) => cartItem.id !== id);
          toast.success("Item removed from cart!");
        }
        saveCartToAppStorage(state.cart);
      }
    },
    clearCart: (state) => {
      state.cart = [];
      AppStorage.remove("cart");
      toast.success("Cart cleared!");
    },
    setOrderData: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.orderData = action.payload;
    },
    setCartModalOpen: (state, action: PayloadAction<boolean>) => {
      state.cartModalIsOpen = action.payload;
    },
    setCoupon: (state, action: PayloadAction<string>) => {
      state.coupon = action.payload;
    },
    setRelatedCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.relatedCartItems = action.payload;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
  updateItemQuantity,
  clearCart,
  setOrderData,
  setCartModalOpen,
  setCoupon,
  setRelatedCartItems,
} = cartSlice.actions;

/**
 * Calculate total for a single cart item including its add-ons and extras
 */
export const calculateItemTotal = (item: CartItem): number => {
  const mainItemTotal = item.price * item.quantity;
  const addOnsTotal = item.addOns.reduce((sum, addOn) => sum + addOn.price * addOn.quantity, 0);
  const extrasTotal = item.extras.reduce((sum, extra) => sum + extra.price * extra.quantity, 0);
  return mainItemTotal + (addOnsTotal + extrasTotal) * item.quantity;
};

// Selectors
export const selectCart = (state: { cart: CartState }) => state.cart.cart;
export const selectCartTotal = (state: { cart: CartState }) =>
  state.cart.cart.reduce((total, item) => total + calculateItemTotal(item), 0);
export const selectCartItemCount = (state: { cart: CartState }) =>
  state.cart.cart.reduce((total, item) => total + item.quantity, 0);

export default cartSlice.reducer;

